import React, { useEffect, useState } from 'react';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import {
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminFormSecondaryAction,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMainText,
  AdminPanelTitle,
  AdminSupportingText,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_TOOLBAR_CLASS,
  adminFormDetailInputClasses,
  adminFormTextareaClasses,
} from '../shared/adminUx';

/**
 * Shared Settings section shell — left subsection nav + right panel, sticky footer, modals.
 */
export function SettingsSectionShell({
  subsections,
  activeId,
  onSelectSubsection,
  children,
  hasChanges = false,
  saving = false,
  canSave = true,
  onSave,
  onCancel,
  showFooter = true,
}) {
  const [discardOpen, setDiscardOpen] = useState(false);
  const [pendingSubsection, setPendingSubsection] = useState(null);

  const requestSubsection = (id) => {
    if (id === activeId) return;
    if (hasChanges) {
      setPendingSubsection(id);
      setDiscardOpen(true);
      return;
    }
    onSelectSubsection(id);
  };

  const confirmDiscard = () => {
    onCancel?.();
    setDiscardOpen(false);
    if (pendingSubsection) {
      onSelectSubsection(pendingSubsection);
      setPendingSubsection(null);
    }
  };

  const keepEditing = () => {
    setDiscardOpen(false);
    setPendingSubsection(null);
  };

  return (
    <div>
      <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
        <div className="flex flex-col lg:flex-row min-h-[420px]">
          <nav
            className="lg:w-[240px] shrink-0 py-3 pr-3 border-b lg:border-b-0 lg:border-r border-lightgray"
            aria-label="Settings subsections"
          >
            <ul className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
              {subsections.map((item) => {
                const active = item.id === activeId;
                return (
                  <li key={item.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => requestSubsection(item.id)}
                      className={`w-full text-left py-2 pr-3 transition-colors whitespace-nowrap ${
                        active ? 'text-primaryblue' : 'text-offblack hover:text-primaryblue'
                      }`}
                    >
                      <AdminMainText as="span" className={active ? 'text-primaryblue' : ''}>
                        {item.label}
                      </AdminMainText>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          <div className="flex-1 min-w-0 p-4 space-y-4">{children}</div>
        </div>
      </DashboardMainContainer>

      {showFooter && hasChanges ? (
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex justify-end gap-3">
            <AdminFormSecondaryAction type="button" onClick={() => setDiscardOpen(true)} disabled={saving}>
              Cancel
            </AdminFormSecondaryAction>
            <AdminHeaderAction onClick={onSave} disabled={saving || !canSave}>
              {saving ? 'Saving…' : 'Save Changes'}
            </AdminHeaderAction>
          </div>
        </div>
      ) : null}

      {discardOpen ? (
        <SettingsConfirmModal
          title="Discard Changes?"
          message="You have unsaved changes in this section."
          confirmLabel="Discard Changes"
          cancelLabel="Keep Editing"
          onCancel={keepEditing}
          onConfirm={confirmDiscard}
        />
      ) : null}
    </div>
  );
}

export function SettingsConfirmModal({
  title,
  message,
  reasonLabel = 'Internal Reason',
  reason,
  onReasonChange,
  reasonRequired = false,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onCancel,
  onConfirm,
  confirming = false,
}) {
  const reasonOk = !reasonRequired || (reason && String(reason).trim().length > 0);
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      onClick={onCancel}
      role="presentation"
    >
      <div
        className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS} max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="px-5 pt-5 pb-3 border-b border-lightgray">
          <AdminPanelTitle className="text-offblack text-center">{title}</AdminPanelTitle>
        </div>
        <div className="px-5 py-4 space-y-3">
          {message ? (
            <AdminSupportingText className="text-offblack text-center">{message}</AdminSupportingText>
          ) : null}
          {reasonRequired || onReasonChange ? (
            <div className="space-y-1">
              <AdminDetailFieldLabel>{reasonLabel}</AdminDetailFieldLabel>
              <textarea
                value={reason || ''}
                onChange={(e) => onReasonChange?.(e.target.value)}
                rows={3}
                className={adminFormTextareaClasses()}
                placeholder="Enter reason"
              />
            </div>
          ) : null}
        </div>
        <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={onCancel} disabled={confirming}>
            {cancelLabel}
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="button" onClick={onConfirm} disabled={confirming || !reasonOk}>
            {confirming ? 'Saving…' : confirmLabel}
          </AdminHeaderAction>
        </div>
      </div>
    </div>
  );
}

export function SettingsField({ label, required, error, children, setupRequired = false }) {
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>
        {label}
        {required ? <span className="text-alertred"> *</span> : null}
        {setupRequired ? <span className="text-alertred"> — Setup required</span> : null}
      </AdminDetailFieldLabel>
      {children}
      {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
    </div>
  );
}

export function SettingsReadOnlyRow({ label, value, mono = false }) {
  const display = value == null || value === '' ? '—' : String(value);
  return (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <input
        type="text"
        value={display}
        readOnly
        className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
      />
    </div>
  );
}

export function SettingsRelatedLink({ children, onClick }) {
  return (
    <div>
      <AdminLinkAction type="button" onClick={onClick}>
        {children}
      </AdminLinkAction>
    </div>
  );
}

export function useSettingsPanelState(initialData, historyContext = null) {
  const [saved, setSaved] = useState(initialData);
  const [draft, setDraft] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const hasChanges = JSON.stringify(saved) !== JSON.stringify(draft);

  const setField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const cancel = () => {
    setDraft(saved);
    setErrors({});
  };

  const save = async (validate, successText, changeReason) => {
    const nextErrors = validate?.(draft) || {};
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return false;
    const previousSnapshot = { ...saved };
    const nextSnapshot = { ...draft };
    const changedKeys = Object.keys(nextSnapshot).filter(
      (key) => JSON.stringify(previousSnapshot[key]) !== JSON.stringify(nextSnapshot[key])
    );
    setSaving(true);
    await new Promise((r) => setTimeout(r, 400));
    setSaved(draft);
    setSaving(false);
    setSuccessMessage(successText || 'Settings updated.');
    if (historyContext && changedKeys.length) {
      try {
        const { emitSettingsChange } = await import('../../../data/changeHistoryEmitters');
        const previousValues = {};
        const newValues = {};
        changedKeys.forEach((key) => {
          previousValues[key] = previousSnapshot[key];
          newValues[key] = nextSnapshot[key];
        });
        emitSettingsChange({
          section: historyContext.section || 'Settings',
          subsection: historyContext.subsection || null,
          previousValues,
          newValues,
          changeReason,
          sourceItemRef: historyContext.sourceItemRef,
          actor: historyContext.actor,
        });
      } catch {
        /* history recording failure must not reverse the source save */
      }
    }
    return true;
  };

  useEffect(() => {
    if (!successMessage) return undefined;
    const t = setTimeout(() => setSuccessMessage(''), 3200);
    return () => clearTimeout(t);
  }, [successMessage]);

  return {
    saved,
    draft,
    setDraft,
    setField,
    hasChanges,
    saving,
    errors,
    setErrors,
    cancel,
    save,
    successMessage,
    setSuccessMessage,
    setSaved,
  };
}

export function SettingsSuccessToast({ message }) {
  if (!message) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[60] rounded-xl bg-offblack text-white px-4 py-3 text-sm shadow-lg">
      {message}
    </div>
  );
}
